// #region imports

// --- Angular
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { of, Observable, Subscription, forkJoin, zip, combineLatest } from 'rxjs';
import { catchError, map, mapTo } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';

// --- HS Code
import { environment } from '../../../../environments/environment';
import {
  ApplicationError,
  RemoteCommand,
  Constants,
  DeleteCommand,
  GroupModel,
  PatchModel,
  PatchOperations,
  RespondentModel,
  SurveyResponseContexts,
  SurveyResponseModel,
  UndoModel,
  UpdateCommand,
  DeleteGroupCommand,
  ErrorMessage,
} from '../../../models';
import { AppService } from '../app.service';
import { OidcService } from '../account/oidc.service';
import { ParticipantGroupsBaseService } from './participant-groups-base.service';
import { ResponseBaseService } from './response-base.service';
import { UserNotificationBaseService } from '../notifications/user-notification-base.service';
import { ParticipantGroupCommandHandler } from './participant-group-command-handler';
import { CommandHandlerResult } from '../system/command-handler';
import { group } from '@angular/animations';

// #endregion

@Injectable({
  providedIn: 'root',
})
export class ParticipantGroupsService
  extends ParticipantGroupsBaseService
  implements OnDestroy
{
  private _surveysSubscription: Subscription = Subscription.EMPTY;
  private _isLoadingGroups: boolean = false;
  private _commandId: number = 0;

  private _identifier: string | null = null;

  constructor(
    private http: HttpClient,
    private responseService: ResponseBaseService,
    private _commandHandler: ParticipantGroupCommandHandler,
    appService: AppService,
    translateService: TranslateService,
    oidcService: OidcService,
    userNotificationService: UserNotificationBaseService
  ) {
    super(translateService, oidcService, appService, userNotificationService);
  }

  override ngOnDestroy(): void {
    if (this._surveysSubscription) {
      this._surveysSubscription.unsubscribe();
      this._surveysSubscription = Subscription.EMPTY;
    }
  }

  loadGroups(identifier: string | null = null, isSilent: boolean = false, forceRefresh: boolean = false): void {
    if (!identifier) {
      identifier = this._identifier;
    } else {
      if (this._identifier === identifier && !forceRefresh) {
        // The survey has not changed, don't reload respondents
        return;
      }
      this._identifier = identifier;
    }
    if (!identifier || this._isLoadingGroups) {
      return;
    }

    this._participantGroups$.next(null);

    if (this._surveysSubscription !== Subscription.EMPTY) {
      this._surveysSubscription.unsubscribe();
    }

    this._surveysSubscription = this.responseService.activeResponse$.subscribe(
      (survey: SurveyResponseModel | null) => {
        if (!survey) {
          return;
        }
        const surveyId = survey.id;

        if (!surveyId) {
          return;
        }

        this._surveysSubscription.unsubscribe();
        this._surveysSubscription = Subscription.EMPTY;

        if (survey.responseContext === SurveyResponseContexts.respondent) {
          return;
        }

        if (!isSilent) {
          this.appService.setLoading(true);
        }
        this._isLoadingGroups = true;

        const url =
          environment.apiConfig.surveyApiUrl +
          environment.apiConfig.participantsGroupsApi +
          `/${surveyId}`;
        this.http
          .get<GroupModel[]>(url)
          .pipe(
            retryBackoff(environment.retryConfig),
            catchError(this._getError.bind(this))
          )
          .subscribe((result: GroupModel[]) => {

            this._isLoadingGroups = false;

            if (!isSilent) {
              this.appService.setLoading(false);
            }

            if (result.length === 0) {
              this._participantGroups$.next([]);
              result.push(this.createGroup(surveyId, survey.numberOfRespondentsPerGroup));
            }
            this._setRespondents(result, survey.numberOfRespondentsPerGroup);
            this._participantGroups$.next(result);
          });
      }
    );
  }

  add(group: GroupModel): void {
    if (!group) {
      return;
    }

    const groups = this._participantGroups$.value;
    if (groups === null) return;

    const newGroup = groups.find(
      (g) => g.id.toLowerCase() === group.id.toLowerCase()
    );

    if (!this._canValidRespondentsBeAdded(group)) {

      if (newGroup) {
        newGroup.groupName = group.groupName;
        this._participantGroups$.next(
          _.cloneDeep(this._participantGroups$.value)
        );
      }
      return;
    } else if (newGroup) {
      newGroup.groupName = group.groupName;
      this._participantGroups$.next(structuredClone(groups));
    }

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.participantsGroupsApi;

    this.appService.setLoading(true);
    const dummyRespondents = group.respondents.filter((r) => !r.isValid);
    group.respondents = group.respondents.filter((r) => r.isValid);

    const groupsToAdd = new Array<GroupModel>();
    groupsToAdd.push(group);

    this.http
      .post<GroupModel[]>(url, groupsToAdd)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._postError.bind(this))
      )
      .subscribe((result) => {
        this.appService.setLoading(false);

        if (result == null || result.length === 0) {
          group.groupName = '';
          return;
        }

        let theGroups = [] as GroupModel[];
        if (this._participantGroups$.value) {
          theGroups = _.cloneDeep(this._participantGroups$.value);
        }

        this._afterGroupsAdded(theGroups, result, dummyRespondents);
        const theGroup = theGroups.find(
          (g) => g.index === group.index
        ) as GroupModel;
        if (theGroup) {
          let isExpanded = false;
          for (const respondent of theGroup.respondents) {
            if (!isExpanded && !respondent.isSet) {
              respondent.isExpanded = true;
              isExpanded = false;
              break;
            }
          }
        }
        this._participantGroups$.next(theGroups);
      });
  }

  saveGroup(group: GroupModel): void {
    if (!group || !group.id) {
      return;
    }

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.participantsGroupsApi +
      `/${group.id}`;
    const patchModel = {
      operation: PatchOperations.replace,
      path: Constants.GROUP_NAME_PATCH_PATH,
      value: group.groupName,
    } as PatchModel;

    this.appService.setLoading(true);

    this.http
      .patch(url, patchModel)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._patchError.bind(this))
      )
      .subscribe(() => {
        this.appService.setLoading(false);
      });
  }

  update(groups: GroupModel[]): void {
    if (!groups || groups.length === 0) {
      return;
    }

    const participantId = groups[0].participantId;

    const newGroups = groups
      .filter((g) => g.isNew)
      .sort((g1, g2) => g1.index - g2.index);
    for (const newGroup of newGroups) {
      if (this._participantGroups$.value) {
        const currentGroups = this._participantGroups$.value;
        const groupFound = currentGroups.find((c) => c.id === newGroup.id);
        if (groupFound) {
          newGroup.isActiveGroup = true;
        } else {
          this.addEmpty(newGroup);
        }
      }
    }
    const groupsToUpdate = groups.filter((g) => !g.isNew);

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.participantsGroupsApi;

    this.appService.setLoading(true);

    this.http
      .put<GroupModel[]>(`${url}/${participantId}`, groupsToUpdate)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._updateError.bind(this))
      )
      .subscribe((updateResult: GroupModel[]) => {
        this.http
          .post<GroupModel[]>(url, newGroups)
          .pipe(
            retryBackoff(environment.retryConfig),
            catchError(this._postError.bind(this))
          )
          .subscribe((result: GroupModel[]) => {
            this.appService.setLoading(false);

            let theGroups = [] as GroupModel[];
            if (this._participantGroups$.value) {
              theGroups = _.cloneDeep(this._participantGroups$.value);
              this._afterGroupsUpdated(theGroups, updateResult);
              this._afterGroupsRemoved(theGroups, updateResult);
            }

            this._afterGroupsAdded(
              theGroups,
              result,
              new Array<RespondentModel>()
            );

            this._setRespondents(theGroups);
            this._participantGroups$.next(theGroups);
          });
      });
  }

  updateGroupName(group: GroupModel): void {
    const groups = this._participantGroups$.value;
    if (!groups) {
      return;
    }
    const updateGroup = groups.find((g) => {
      return g.id === group.id;
    });
    if (updateGroup) {
      updateGroup.groupName = group.groupName;
      this._participantGroups$.next(groups.splice(0));
    }
  }

  delete(group: GroupModel): void {
    if (!group) {
      return;
    }

    if (!this._participantGroups$.value) {
      return;
    }

    if (group.isSet) {
      const url =
        environment.apiConfig.surveyApiUrl +
        environment.apiConfig.participantsGroupsApi +
        `/${group.id}`;

      this.appService.setLoading(true);

      this.http
        .delete(url)
        .pipe(
          retryBackoff(environment.retryConfig),
          catchError(this._deleteError.bind(this))
        )
        .subscribe(() => {
          this.appService.setLoading(false);
        });
    }

    const groupIndex = this._participantGroups$.value.findIndex((g) => {
      return g.id === group.id;
    });

    if (groupIndex >= 0) {
      const groups = _.cloneDeep(this._participantGroups$.value);
      groups.splice(groupIndex, 1);
      const activeGroupIndex = groups.findIndex((g) => g.isActiveGroup);
      if (activeGroupIndex < 0 && groups.length > 0) {
        groups[0].isActiveGroup = true;
      }
      this._participantGroups$.next(groups);
    }
  }

  override saveGroupChanges(
    deletedGroups: GroupModel[],
    addedGroups: GroupModel[],
    updatedGroups: GroupModel[],
    managedGroups: GroupModel[],
    participantId: string): void {
    const deletes = new Array<Observable<undefined>>();
    if (deletedGroups && deletedGroups.length > 0) {
      const d = deletedGroups.map(dg => {
        const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.participantsGroupsApi + `/${dg.id}`;
        return this.http.delete<undefined>(url)
          .pipe(
            retryBackoff(environment.retryConfig),
            catchError(this._deleteError.bind(this))
          );
      });
      deletes.splice(0, 0, ...d);
    }

    let adds: Observable<GroupModel[]> | undefined;
    if (addedGroups && addedGroups.length > 0) {
      const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.participantsGroupsApi;
      adds = this.http.post<GroupModel[]>(url, addedGroups)
        .pipe(
          retryBackoff(environment.retryConfig),
          catchError(this._postError.bind(this))
        );
    }

    let updates: Observable<GroupModel[]> | undefined;
    if (updatedGroups && updatedGroups.length > 0) {
      const url = environment.apiConfig.surveyApiUrl + environment.apiConfig.participantsGroupsApi;
      const participantId = updatedGroups.at(0)?.participantId;
      updates = this.http.put<GroupModel[]>(`${url}/${participantId}`, updatedGroups)
        .pipe(
          retryBackoff(environment.retryConfig),
          catchError(this._updateError.bind(this))
        );
    }

    const allChanges: { [key: string]: any} = {};
    let hasChanges: boolean = false;
    if (deletes && deletes.length > 0) {
      hasChanges = true;
      deletes.forEach((d, index) => {
        allChanges['delete' + index] = d;
      })
    }
    if (adds) {
      hasChanges = true;
      allChanges['adds'] = adds;
    }
    if (updates) {
      hasChanges = true;
      allChanges['updates'] = updates;
    }

    if (!hasChanges) return;

    this.appService.setLoading(true);

    combineLatest(allChanges)
    .subscribe((result: { [key: string]: any}) => {
      this.appService.setLoading(false);

      if (!result) return;

      const groupChanges: GroupModel[] = [];

      if (result.hasOwnProperty('adds') && result['adds']) {
        const addResults = result['adds'] as GroupModel[];
        groupChanges.splice(0, 0, ...addResults)
      }

      if (result.hasOwnProperty('updates') && result['updates']) {
        const updateResults = result['updates'] as GroupModel[];
        groupChanges.splice(groupChanges.length, 0, ...updateResults);
      }

      // ToDo: Add a summary view to show what changes were carried out.
      // ToDo: Put together a comprehensive error report for any errors that may have occurred.

      managedGroups.forEach(mg => mg.isDirty = false);
      this._setRespondents(managedGroups);
      this._participantGroups$.next(managedGroups);
      this.loadGroups(participantId, true);
    });
  }

  private _getError(err: any): Observable<GroupModel[]> {

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'ParticipantGroupsService',
      true,
      err
    );

    this.appService.setLastError(error);

    return of([] as GroupModel[]);
  }

  private _postError(err: any): Observable<GroupModel[]> {

    const errorCode: number = err?.status && err?.status === 409 ?
      ErrorMessage.respondentManagementDuplicateGroupNameErrorCode:
      ErrorMessage.respondentManagementLoadGroupsErrorCode;

    const error: ApplicationError = ApplicationError.create(
      errorCode,
      'ParticipantGroupsService',
      false,
      err
    );

    this.appService.setLastError(error);
    return of([] as GroupModel[]);
  }

  private _patchError(err: any): Observable<undefined> {

    const errorCode: number = err?.status && err?.status === 409 ?
      ErrorMessage.respondentManagementDuplicateGroupNameErrorCode:
      ErrorMessage.respondentManagementLoadGroupsErrorCode;

    const error: ApplicationError = ApplicationError.create(
      errorCode,
      'ParticipantGroupsService',
      false,
      err
    );

    this.appService.setLastError(error);
    return of();
  }

  private _deleteError(err: any): Observable<undefined> {

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'ParticipantGroupsService',
      false,
      err
    );

      this.appService.setLastError(error);
      return of();
    }

  private _updateError(err: any): Observable<any> {

    let detailedMessage: string = '';
    let statusCode = -1;
    let showToast = true;

    if (err && err instanceof HttpErrorResponse) {
      statusCode = err.status;
      if (statusCode < 500) showToast = false;
      // Note the above - we need more detailed messages, but they require translation.\
      // First step is to decide what to tell users in each error case (404 means not found, etc.)
      detailedMessage += `<br/>${err.statusText}<br/>${err.message}`;
    }

    const error = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'ParticipantGroupsService',
      false,
      err,
      detailedMessage,
      undefined,
      statusCode,
      undefined,
      showToast
    );

    this.appService.setLastError(error);

    return of(null);
  }

  private _canValidRespondentsBeAdded(group: GroupModel): boolean {
    const validRespondents = group.respondents.filter((r) => r.isValid);
    let canAdd = true;

    for (const respondent of validRespondents) {
      if (
        !respondent.firstName ||
        !respondent.lastName ||
        !respondent.emailAddress ||
        !respondent.languageId
      ) {
        canAdd = false;
        break;
      }
    }

    return canAdd;
  }

  private _afterGroupsUpdated(
    groups: GroupModel[],
    updatedGroups: GroupModel[]
  ) {
    if (!groups || !updatedGroups) {
      return;
    }

    for (const group of updatedGroups) {
      const theGroup = groups.find((g) => g.id === group.id);
      if (!theGroup) {
        continue;
      }
      theGroup.groupName = group.groupName;
      theGroup.id = group.id;
      theGroup.isSet = true;

      for (const respondent of group.respondents) {
        const theRespondent = theGroup.respondents.find(
          (r) => r.groupIndex === respondent.groupIndex
        );
        if (!theRespondent) {
          continue;
        }
        theRespondent.groupIndex = respondent.groupIndex;
        theRespondent.id = respondent.id;
        theRespondent.isSet = true;
        theRespondent.isValid = true;
        theRespondent.status = respondent.status;
        theRespondent.personalMessage = respondent.personalMessage;
        theRespondent.firstName = respondent.firstName;
        theRespondent.lastName = respondent.lastName;
        theRespondent.emailAddress = respondent.emailAddress;
        theRespondent.participantId = respondent.participantId;
        theRespondent.percentageComplete = respondent.percentageComplete;
        theRespondent.personalMessage = respondent.personalMessage;
        theRespondent.invitationType = respondent.invitationType;
        theRespondent.errorMessage = respondent.errorMessage;
        theRespondent.avatar = respondent.avatar;
        theRespondent.languageId = respondent.languageId;
        theRespondent.locale = respondent.locale;
      }
    }
  }

  private _afterGroupsAdded(
    groups: GroupModel[],
    newGroups: GroupModel[],
    dummyRespondents: RespondentModel[]
  ) {
    if (!newGroups) {
      return;
    }

    for (const newGroup of newGroups) {
      const foundGroup = groups.find((g) => {
        return g.index === newGroup.index;
      });

      if (!foundGroup) {
        continue;
      }

      let group = foundGroup;
      if (group) {
        group.participantId = newGroup.participantId;
        foundGroup.respondents = new Array<RespondentModel>()
          .concat(newGroup.respondents, dummyRespondents)
          .sort((a, b) => a.groupIndex - b.groupIndex);
      } else {
        group = this.createGroup(newGroup.participantId);
        groups.push(group);
      }

      group.id = newGroup.id;
      group.isSet = true;
      group.groupName = newGroup.groupName;

      for (const respondent of group.respondents) {
        respondent.groupId = group.id;
        if (respondent.isSet) {
          respondent.isValid = true;
        }
      }
    }
  }

  private _afterGroupsRemoved(
    groups: GroupModel[],
    updatedGroups: GroupModel[]
  ) {
    if (!groups || !updatedGroups) {
      return;
    }
    for (const group of updatedGroups) {
      const theGroup = groups.find((g) => g.id === group.id);
      if (!theGroup) {
        continue;
      }
      const theUpdatedGroup = updatedGroups.find((g) => g.id === theGroup.id);
      if (!theUpdatedGroup) {
        continue;
      }
      theGroup.respondents = theUpdatedGroup.respondents;
    }
  }
}
